/**
 * Impressão digital da senha-mestra.
 *
 * Três símbolos coloridos derivados de HMAC-SHA-256(mestra, rótulo fixo).
 * Serve para o usuário perceber um erro de digitação na mestra ANTES de gerar
 * — sem essa checagem, uma mestra errada produz silenciosamente uma senha
 * errada, e o usuário só descobre no login.
 *
 * Trade-off consciente: são 24 bits de um hash rápido da mestra visíveis na
 * tela. Numa foto ou gravação de tela, isso dá a um atacante um oráculo barato
 * para testar palpites de mestra offline. Por isso é desligável na UI, e por
 * isso ela nunca deve ser compartilhada. É o mesmo compromisso adotado pelo
 * LessPass.
 */

import { ALGO_VERSION } from './spec'
import { normalizeSecret } from './salt'

export const FINGERPRINT_LABEL = `${ALGO_VERSION}:fingerprint`

/** Quantidade de formas disponíveis; o componente mapeia índice → SVG. */
export const FINGERPRINT_SHAPES = 16

/** Paleta legível nos dois temas. */
export const FINGERPRINT_COLORS = [
  '#F43F5E',
  '#FB7185',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#10B981',
  '#14B8A6',
  '#06B6D4',
  '#0EA5E9',
  '#6366F1',
  '#8B5CF6',
  '#A855F7',
  '#D946EF',
  '#EC4899',
] as const

export interface FingerprintGlyph {
  shape: number
  color: string
}

/**
 * Calculada com HMAC (rápido) e não com Argon2id: precisa responder enquanto o
 * usuário digita. Isso é seguro apenas porque o resultado nunca sai da tela.
 */
export async function masterFingerprint(master: string): Promise<FingerprintGlyph[]> {
  const encoder = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(normalizeSecret(master)) as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(FINGERPRINT_LABEL) as BufferSource,
  )
  const bytes = new Uint8Array(signature)

  const glyphs: FingerprintGlyph[] = []
  for (let i = 0; i < 3; i += 1) {
    glyphs.push({
      shape: bytes[i * 2]! % FINGERPRINT_SHAPES,
      color: FINGERPRINT_COLORS[bytes[i * 2 + 1]! % FINGERPRINT_COLORS.length]!,
    })
  }
  return glyphs
}
